import React, { useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm } from './supabaseService';

const LOGO_URL = '/logo.jpg';

const initialState = {
  companyName: '',
  jobDate: '',
  // Kitchen Area
  kitchen: {
    workTops: 'Yes', floors: 'Yes', sink: 'Yes', frontCupboards: 'Yes', insideCupboards: 'Yes', oven: 'Yes', walls: 'Yes', skirtingBoards: 'Yes', windows: 'Yes', accessories: 'Yes', ceilingLights: 'Yes', radiator: 'Yes', chairsTable: 'Yes', fridgeOutside: 'Yes', fridgeInside: 'Yes', splashBack: 'Yes',
    recommendations: '',
  },
  // Bathrooms
  bathrooms: {
    men: 'Yes', women: 'Yes', disabled: 'Yes', offensiveOdor: 'Yes', accessories: 'Yes', ceilingLights: 'Yes', radiator: 'Yes', showerScreen: 'Yes', floors: 'Yes', walls: 'Yes', handBasin: 'Yes', toiletBowl: 'Yes', urinals: 'Yes', binEmptied: 'Yes',
    recommendations: '',
  },
  // Office Area / Storeroom
  office: {
    desks: 'Yes', monitors: 'Yes', furniture: 'Yes', carpet: 'Yes', skirtingBoards: 'Yes', binsEmptied: 'Yes', windows: 'Yes', telephone: 'Yes',
    recommendations: '',
  },
  extras: '', // Add extras field
  extrasRows: [
    { description: '', yes: false, no: false },
  ],
  assessorName: '',
  assessmentDate: '',
  department: '',
  contactInfo: '',
};

const sectionDefs = [
  {
    key: 'kitchen',
    title: 'KITCHEN AREA',
    items: [
      ['workTops', 'Work Tops Clean'],
      ['floors', 'Floors Clean'],
      ['sink', 'Sink Clean'],
      ['frontCupboards', 'Front Cupboards Clean'],
      ['insideCupboards', 'Inside Cupboards Clean'],
      ['oven', 'Oven Clean'],
      ['walls', 'Walls Clean'],
      ['skirtingBoards', 'Skirting Boards Clean'],
      ['windows', 'Windows Clean'],
      ['accessories', 'Accessories Clean'],
      ['ceilingLights', 'Ceiling Lights Clean'],
      ['radiator', 'Radiator Clean'],
      ['chairsTable', 'Chairs / Table Clean'],
      ['fridgeOutside', 'Fridge Outside Clean'],
      ['fridgeInside', 'Fridge Inside Clean'],
      ['splashBack', 'Splash Back Clean'],
    ],
  },
  {
    key: 'bathrooms',
    title: 'BATHROOMS',
    items: [
      ['men', 'Men'],
      ['women', 'Women'],
      ['disabled', 'Disabled'],
      ['offensiveOdor', 'Offensive Odor'],
      ['accessories', 'Accessories Clean'],
      ['ceilingLights', 'Ceiling Lights Clean'],
      ['radiator', 'Radiator Clean'],
      ['showerScreen', 'Shower / Screen Clean'],
      ['floors', 'Floors Clean'],
      ['walls', 'Walls Clean'],
      ['handBasin', 'Hand Basin / Tap / Mirror Clean'],
      ['toiletBowl', 'Toilet Bowl Clean'],
      ['urinals', 'Urinals Clean'],
      ['binEmptied', 'Bin Emptied'],
    ],
  },
  {
    key: 'office',
    title: 'OFFICE AREA / STOREROOM',
    items: [
      ['desks', 'Desks Clean'],
      ['monitors', 'Monitors Clean'],
      ['furniture', 'Furniture Clean'],
      ['carpet', 'Carpet Clean'],
      ['skirtingBoards', 'Skirting Boards / Ledges Clean'],
      ['binsEmptied', 'Bins Emptied'],
      ['windows', 'Windows Clean'],
      ['telephone', 'Telephone'],
    ],
  },
];

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const styles = {
  container: {
    maxWidth: 900,
    margin: '40px auto',
    padding: 0,
    background: '#f6f7fb',
    borderRadius: 0,
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'auto',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 0 80px 0', // extra bottom for keyboard
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  card: {
    background: 'linear-gradient(135deg, #fff 80%, #ffe5e5 100%)',
    borderRadius: 18,
    boxShadow: '0 6px 32px rgba(255,0,0,0.08), 0 1.5px 6px rgba(0,0,0,0.04)',
    padding: '28px 18px',
    width: '100%',
    maxWidth: 430,
    margin: '0 auto 28px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    border: '1.5px solid #f2dede',
  },
  header: {
    textAlign: 'center',
    marginBottom: 0,
  },
  logo: {
    width: 80,
    height: 'auto',
    marginBottom: 8,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(255,0,0,0.08)',
  },
  formTitle: {
    fontWeight: 900,
    fontSize: '2.2rem',
    color: '#c00',
    margin: '0 0 18px 0',
    letterSpacing: 0.5,
    lineHeight: 1.1,
  },
  section: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    padding: '18px 12px',
    marginBottom: 18,
    border: '1.5px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    marginBottom: 8,
    color: '#b00',
    letterSpacing: 0.5,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 14,
    width: '100%',
  },
  label: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 6,
    color: '#222',
    letterSpacing: 0.1,
  },
  input: {
    width: '100%',
    padding: '16px 12px',
    fontSize: 18,
    border: '1.5px solid #ccc',
    borderRadius: 10,
    background: '#f9f9f9',
    marginBottom: 0,
    boxSizing: 'border-box',
    transition: 'border 0.2s',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '16px 12px',
    fontSize: 18,
    borderRadius: 10,
    border: '1.5px solid #ccc',
    background: '#fff',
    boxSizing: 'border-box',
    marginBottom: 0,
    transition: 'border 0.2s',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    minHeight: 80,
    padding: '16px 12px',
    fontSize: 18,
    border: '1.5px solid #ccc',
    borderRadius: 10,
    background: '#f9f9f9',
    marginBottom: 0,
    boxSizing: 'border-box',
    resize: 'vertical',
    transition: 'border 0.2s',
    outline: 'none',
  },
  recommendationsLabel: {
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
    color: '#b00',
    fontSize: 16,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    marginBottom: 10,
    fontSize: 16,
  },
  th: {
    background: '#f2f2f2',
    fontWeight: 700,
    padding: 10,
    border: '1px solid #ccc',
    textAlign: 'left',
    fontSize: 16,
  },
  td: {
    border: '1px solid #e0e0e0',
    padding: 10,
    fontSize: 16,
    background: '#fff',
  },
  button: {
    padding: '18px 0',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontWeight: 800,
    fontSize: 22,
    cursor: 'pointer',
    marginTop: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    maxWidth: 430,
    boxShadow: '0 2px 8px rgba(255,0,0,0.08)',
    letterSpacing: 1,
    transition: 'background 0.2s, transform 0.1s',
    outline: 'none',
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    width: '100%',
    background: 'linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%)',
    zIndex: 10,
    padding: 0,
    margin: 0,
    boxShadow: '0 -2px 8px rgba(255,0,0,0.08)',
    borderRadius: '0 0 18px 18px',
    display: 'flex',
    justifyContent: 'center',
  },
};

const mobileStyle = `
@media (max-width: 600px) {
  .qrf-container {
    padding: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: #f6f7fb !important;
  }
  .qrf-scroll {
    padding: 12px 0 90px 0 !important;
  }
  .qrf-card {
    padding: 18px 6px !important;
    border-radius: 12px !important;
    margin-bottom: 18px !important;
    max-width: 100vw !important;
  }
  .qrf-logo {
    width: 60px !important;
    margin-bottom: 4px !important;
  }
  .qrf-title {
    font-size: 1.3rem !important;
  }
  .qrf-formTitle {
    font-size: 1.5rem !important;
  }
  .qrf-sectionTitle {
    font-size: 1.05rem !important;
  }
  .qrf-label {
    font-size: 13px !important;
    margin-bottom: 3px !important;
  }
  .qrf-table td, .qrf-table th {
    font-size: 13px !important;
    padding: 6px !important;
  }
  .qrf-button {
    font-size: 16px !important;
    padding: 14px 0 !important;
    border-radius: 10px !important;
    max-width: 100vw !important;
  }
  .qrf-textarea, .qrf-input, .qrf-select {
    font-size: 16px !important;
    padding: 12px !important;
    border-radius: 8px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }
  .qrf-stickyFooter {
    position: fixed !important;
    left: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    border-radius: 0 !important;
    z-index: 1000 !important;
    box-shadow: 0 -2px 8px rgba(255,0,0,0.08) !important;
    background: linear-gradient(90deg, #ff0000 60%, #ff4d4d 100%) !important;
    padding: 0 !important;
    margin: 0 !important;
    display: flex !important;
    justify-content: center !important;
  }
}
@media (max-width: 400px) {
  .qrf-title {
    font-size: 1.1rem !important;
  }
  .qrf-formTitle {
    font-size: 1.1rem !important;
  }
  .qrf-sectionTitle {
    font-size: 0.95rem !important;
  }
  .qrf-label {
    font-size: 11px !important;
  }
}
`;

export default function QualityReviewForm() {
  const [form, setForm] = useState(initialState);
  const pdfRef = useRef();

  const handleInput = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleText = (section, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        recommendations: value,
      },
    }));
  };

  const handleHeader = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleExtras = (value) => {
    setForm((prev) => ({ ...prev, extras: value }));
  };

  const handleExtrasRowChange = (idx, key, value) => {
    setForm(prev => {
      const newRows = prev.extrasRows.map((row, i) =>
        i === idx ? { ...row, [key]: value } : row
      );
      return { ...prev, extrasRows: newRows };
    });
  };
  const addExtrasRow = () => {
    setForm(prev => ({
      ...prev,
      extrasRows: [
        ...prev.extrasRows,
        { description: '', yes: false, no: false },
      ],
    }));
  };
  const removeExtrasRow = idx => {
    setForm(prev => ({
      ...prev,
      extrasRows: prev.extrasRows.filter((_, i) => i !== idx),
    }));
  };

  // PDF template (hidden)
  const PDFTemplate = (
    <div
      ref={pdfRef}
      style={{
        width: 540,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '2px 18px 24px 18px',
        color: '#222',
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        display: 'block',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        * { box-sizing: border-box; }
        table { table-layout: fixed; width: 100%; margin-bottom: 12px; }
        th, td { word-break: break-word; overflow-wrap: break-word; font-size: 11px; padding: 4px 6px; }
        th { font-size: 12px; background: #f2f2f2; border-bottom: 2px solid #b00; }
        tr:nth-child(even) td { background: #faf7f7; }
        div, td, th { max-width: 100%; }
        .pdf-section { margin-bottom: 16px; page-break-inside: avoid; break-inside: avoid; }
        .pdf-section-title { font-weight: 800; font-size: 14px; color: #b00; margin-bottom: 4px; letter-spacing: 0.5px; }
        .pdf-divider { border-bottom: 1.5px solid #eee; margin: 3px 0 12px 0; }
        tr { page-break-inside: avoid !important; break-inside: avoid !important; }
        .pdf-header { page-break-inside: avoid; break-inside: avoid; margin-bottom: 12px; }
        .pdf-info-table { margin-bottom: 12px; }
        .pdf-recommendations { margin-top: 8px; }
      `}</style>
      <div className="pdf-header" style={{ textAlign: 'center', marginBottom: 8, marginTop: 0 }}>
        <img src={LOGO_URL} alt="Logo" style={{ width: 70, height: 'auto', marginBottom: 2, marginTop: 0 }} />
        <div className="pdf-divider" />
        <h2 style={{ color: '#b00', fontWeight: 900, fontSize: 16, margin: 0, marginBottom: 8, letterSpacing: 0.5 }}>MarriedtotheMop N/W Ltd. Quality Review Form</h2>
      </div>
      <table className="pdf-info-table" style={{ width: '100%', marginBottom: 12 }}>
        <tbody>
          <tr><td style={{ fontWeight: 700, fontSize: 11 }}>Assessor Name:</td><td style={{ fontSize: 11 }}>{form.assessorName}</td></tr>
          <tr><td style={{ fontWeight: 700, fontSize: 11 }}>Assessment Date:</td><td style={{ fontSize: 11 }}>{form.assessmentDate}</td></tr>
          <tr><td style={{ fontWeight: 700, fontSize: 11 }}>Department/Location:</td><td style={{ fontSize: 11 }}>{form.department}</td></tr>
          <tr><td style={{ fontWeight: 700, fontSize: 11 }}>Contact Information:</td><td style={{ fontSize: 11 }}>{form.contactInfo}</td></tr>
        </tbody>
      </table>
      {sectionDefs.map(section => {
        const groups = chunkArray(section.items, 8);
        return (
          <div key={section.key} className="pdf-section">
            <div className="pdf-section-title">{section.title}</div>
            {groups.map((group, gidx) => (
              <table key={gidx} style={{ marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ccc', fontWeight: 700, textAlign: 'left', fontSize: 11 }}>Item</th>
                    <th style={{ border: '1px solid #ccc', fontWeight: 700, textAlign: 'center', fontSize: 11 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map(([key, label]) => {
                    const value = form[section.key][key];
                    let displayValue = '';
                    if (value === 'Yes') displayValue = '✓';
                    else if (value === 'No') displayValue = '✗';
                    else displayValue = value || '';
                    return (
                      <tr key={key}>
                        <td style={{ border: '1px solid #ccc', fontSize: 10 }}>{label}</td>
                        <td style={{ border: '1px solid #ccc', textAlign: 'center', fontSize: 11 }}>{displayValue}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ))}
            <div className="pdf-recommendations" style={{ fontWeight: 700, marginBottom: 2, fontSize: 11 }}>Recommendations:</div>
            <div style={{ border: '1px solid #ccc', borderRadius: 3, minHeight: 16, padding: 3, fontSize: 10 }}>{form[section.key].recommendations || 'No recommendations'}</div>
          </div>
        );
      })}
      <div className="pdf-section">
        <div className="pdf-section-title">Extras</div>
        <table style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: '80%', fontSize: 11 }}>Description</th>
              <th style={{ textAlign: 'center', width: '10%', fontSize: 11 }}>Yes</th>
              <th style={{ textAlign: 'center', width: '10%', fontSize: 11 }}>No</th>
            </tr>
          </thead>
          <tbody>
            {form.extrasRows.map((row, idx) => (
              <tr key={idx}>
                <td style={{ fontSize: 10 }}>{row.description}</td>
                <td style={{ textAlign: 'center', fontSize: 11 }}>{row.yes ? '✓' : ''}</td>
                <td style={{ textAlign: 'center', fontSize: 11 }}>{row.no ? '✓' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const exportPDF = async () => {
    try {
      // Render the PDF template to a hidden div, then export
      const container = document.createElement('div');
      document.body.appendChild(container);
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.appendChild(pdfRef.current);
      
      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .set({
          margin: [0.3, 0.3, 0.3, 0.3],
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true
          },
          jsPDF: { 
            unit: 'in', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          },
        })
        .from(pdfRef.current)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Quality_Review_Form_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'quality-review',
        title: 'Quality Review Form',
        location: form.location || 'Not specified',
        createdBy: form.staffName || 'Not specified',
        staffName: form.staffName || 'Not specified',
        date: form.date || currentDate,
        time: form.time || 'Not specified',
        companyName: form.companyName || 'Not specified',
        address: form.address || 'Not specified',
        postcode: form.postcode || 'Not specified',
        phone: form.phone || 'Not specified',
        email: form.email || 'Not specified',
        contactPerson: form.contactPerson || 'Not specified',
        contactPhone: form.contactPhone || 'Not specified',
        contactEmail: form.contactEmail || 'Not specified',
        cleaningType: form.cleaningType || 'Not specified',
        cleaningFrequency: form.cleaningFrequency || 'Not specified',
        startTime: form.startTime || 'Not specified',
        finishTime: form.finishTime || 'Not specified',
        totalHours: form.totalHours || 'Not specified',
        totalCost: form.totalCost || 'Not specified',
        paymentMethod: form.paymentMethod || 'Not specified',
        paymentTerms: form.paymentTerms || 'Not specified',
        invoiceNumber: form.invoiceNumber || 'Not specified',
        invoiceDate: form.invoiceDate || 'Not specified',
        dueDate: form.dueDate || 'Not specified',
        notes: form.notes || 'Not specified',
        recommendations: form.recommendations || 'Not specified',
        extrasRows: form.extrasRows || []
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      document.body.removeChild(container);
      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={styles.container} className="qrf-container">
      <style>{mobileStyle}</style>
      <div style={styles.scrollArea} className="qrf-scroll">
        <div style={styles.card} className="qrf-card">
          <div style={styles.header}>
            <img src={LOGO_URL} alt="Logo" style={styles.logo} className="qrf-logo" />
            <div style={styles.formTitle} className="qrf-formTitle">Risk Assessment Form</div>
          </div>
          <div style={styles.field}><label style={styles.label} className="qrf-label">Assessor Name:</label><input style={styles.input} className="qrf-input" name="assessorName" value={form.assessorName || ''} onChange={e => handleHeader('assessorName', e.target.value)} /></div>
          <div style={styles.field}><label style={styles.label} className="qrf-label">Assessment Date:</label><input style={styles.input} className="qrf-input" name="assessmentDate" value={form.assessmentDate || ''} onChange={e => handleHeader('assessmentDate', e.target.value)} /></div>
          <div style={styles.field}><label style={styles.label} className="qrf-label">Department/Location:</label><input style={styles.input} className="qrf-input" name="department" value={form.department || ''} onChange={e => handleHeader('department', e.target.value)} /></div>
          <div style={styles.field}><label style={styles.label} className="qrf-label">Contact Information:</label><input style={styles.input} className="qrf-input" name="contactInfo" value={form.contactInfo || ''} onChange={e => handleHeader('contactInfo', e.target.value)} /></div>
        </div>
        {sectionDefs.map(section => {
          const groups = chunkArray(section.items, 5);
          return (
            <div key={section.key} style={styles.section} className="qrf-section">
              <div style={styles.sectionTitle} className="qrf-sectionTitle">{section.title}</div>
              <table style={styles.table} className="qrf-table">
                {groups.map((group, gidx) => (
                  <tbody key={gidx} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    {group.map(([key, label]) => (
                      <tr key={key}>
                        <td style={styles.td}>{label}</td>
                        <td style={styles.td}>
                          <select
                            value={form[section.key][key]}
                            onChange={e => handleInput(section.key, key, e.target.value)}
                            style={styles.select}
                            className="qrf-select"
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
              <div style={styles.recommendationsLabel}>Recommendations:</div>
              <textarea
                style={styles.textarea}
                value={form[section.key].recommendations}
                onChange={e => handleText(section.key, e.target.value)}
                placeholder="No recommendations"
              />
            </div>
          );
        })}
        {/* Extras Section */}
        <div className="qrf-section" style={styles.section}>
          <div className="qrf-sectionTitle" style={styles.sectionTitle}>Extras</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr>
                <th style={{ width: '80%' }}>Description</th>
                <th colSpan={2} style={{ textAlign: 'center', width: '20%' }}>Yes No</th>
              </tr>
            </thead>
            <tbody>
              {form.extrasRows.map((row, idx) => (
                <tr key={idx}>
                  <td><input style={{ ...styles.input, width: '100%' }} value={row.description} onChange={e => handleExtrasRowChange(idx, 'description', e.target.value)} /></td>
                  <td style={{ textAlign: 'center', width: 40 }}><input type="checkbox" checked={row.yes} onChange={e => handleExtrasRowChange(idx, 'yes', e.target.checked)} /></td>
                  <td style={{ textAlign: 'center', width: 40 }}><input type="checkbox" checked={row.no} onChange={e => handleExtrasRowChange(idx, 'no', e.target.checked)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" style={{ width: '100%', background: '#b00', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 6, boxShadow: '0 2px 8px rgba(255,0,0,0.08)' }} onClick={addExtrasRow}>+ Add Row</button>
        </div>
        {/* Hidden PDF template for export only */}
        <div style={{ display: 'none' }}>{PDFTemplate}</div>
        <div style={styles.stickyFooter} className="qrf-stickyFooter">
          <button style={styles.button} className="qrf-button" onClick={exportPDF}>Download PDF</button>
        </div>
      </div>
    </div>
  );
}
